// custom component (not registry-generated)
import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'

export { default as Button } from './Button.vue'

// STRUCTURAL FORK (2026-08-17, escalation ladder step 3): a `loading` prop.
// The KUI migration needs it — KUICoreButton carries `loading` on 47 call
// sites and composing a spinner at each one would scatter the same markup
// (and the same width-jump bug) across the app. Behaviour follows KUI's: the
// label stays in the layout but goes `invisible` and a centred Spinner sits on
// top, so the button does NOT resize when it starts working. Loading also
// implies non-interactive — `pointer-events-none` guarantees it regardless of
// what a consumer passes for `disabled`, and `aria-busy` announces it. The
// non-loading DOM is byte-identical to upstream's (plain `<slot />`), so this
// costs nothing for the buttons that never load. NOTE: `loading` + `as-child`
// is unsupported — reka's asChild contract wants a single child and the
// loading branch renders two.

// `chip` (2026-10-15, Learn IA filter chips): a white card-filled outline pill
// for a filter chip that is not set, so it stands off the off-white page.
// The set state is `default` (ink), and a pressed-state set of chips is
// ToggleGroup `variant="chip"`.
// `outline` (profile IA, 2026-10-23): an ink border with no fill and no shadow,
// so it sits on whatever surface it's on (tinted cards, the off-white page).

// Brand pass (workshop 2026-07-17): pill default — rounded-full on every size.
// PROPORTIONAL SIZE SCALE (design review 2026-07-20, REOPENS the 2026-07-19
// "middle-ground / calm 16px" call): against the toolkit's oversized display
// type (headings 30–160px), calm 14–16px button text got LOST — proven by the
// prize-draw card comparison (an xl DS button dwarfed by a 30px heading). The
// text now grows WITH height, anchored to the Brand Toolkit's own BUTTON
// specs (standard button 20px Medium @ ~54px; the 36px "CTA" style is a
// display/headline treatment, NOT a button — real toolkit buttons top out
// ~20-21px):
//   default/sm 14px · lg 16px · xl 20px (was 16) · 2xl 24px (new hero step).
// Icon glyphs scale per tier (size-4, size-5 at xl/2xl). The hero CTA
// stays `variant="secondary"` + the top tier (lime pill), one per view; block
// CTAs pair with `class="w-full"`. Lime is never an outline or ring (boundary
// ≈1.5:1 fails WCAG 1.4.11; text-bearing lime FILLS are fine, ink-on-lime
// 13.3:1).
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium transition-all disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=\'size-\'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:ring-2 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border border-foreground bg-transparent hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        chip:
          'border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        'default': 'h-9 px-4 py-2 text-sm has-[>svg]:px-3',
        'sm': 'h-8 gap-1.5 px-3 text-sm has-[>svg]:px-2.5',
        'lg': 'h-10 px-6 text-base has-[>svg]:px-4',
        'xl': 'h-14 px-8 text-xl font-semibold has-[>svg]:px-6 [&_svg:not([class*=\'size-\'])]:size-5',
        '2xl': 'h-18 px-10 text-2xl font-semibold has-[>svg]:px-8 [&_svg:not([class*=\'size-\'])]:size-6',
        'icon': 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)
export type ButtonVariants = VariantProps<typeof buttonVariants>
